import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

/**
 * Super-admin credit attribution (al-user-management migration 048).
 *
 * Reads `credit_activity_events` — one row per delivered service, with the
 * agent, course and person it belonged to. NOT a billing source: invoices come
 * from `credit_activity_log`. The `coverage_pct` in the summary compares the
 * two, so a delivery path that stops booking events shows up as a number
 * instead of silently under-reporting.
 *
 * Customer view: every role — super_admin included — gets the tenant from
 * its own token; the backend enforces it (a foreign `client_id` is a 403).
 * Platform-wide numbers belong on the super_admin Token-Analytics page.
 *
 * On `userBackend`, not `tutorsBackend`: the credit tables belong to
 * al-user-management.
 */

export interface CreditFilters {
  start?: string;
  end?: string;
  client_id?: string;
  bot_id?: string;
  course_id?: string;
  area?: string;
  activity_type?: string;
  actor_kind?: string;
  internal_only?: boolean;
}

export interface CreditSummary {
  period: { start: string; end: string };
  credits_milli: number;
  events: number;
  /** Platform-only: absent for customers. */
  clients?: number;
  learners: number;
  bots: number;
  total_tokens: number;
  /** Platform-only: absent in the customer view. */
  internal_credits_milli?: number;
  /** Platform-only: absent in the customer view. */
  unattributed_credits_milli?: number;
  learner_credits_milli: number;
  /** Learner credits ÷ learners. Never the grand total ÷ learners — machine
   *  work (creator, parsing, sync) has no learner behind it. */
  avg_credits_milli_per_learner: number | null;
  /** Platform-only: absent for customers. */
  billed_credits_milli?: number;
  /** Events ÷ billing ledger for the same window. Well below 100 means a
   *  delivery path books no event. Platform-only: absent for customers. */
  coverage_pct?: number | null;
  first_event: string | null;
  /** Completed payments in the window — plan invoices AND credit purchases
   *  (both land in `payments`). EUR, as paid. */
  payments_eur: number;
  /** Same-length window directly before. 0 / null = nothing to compare. */
  previous: {
    credits_milli: number;
    learners: number;
    avg_credits_milli_per_learner: number | null;
    payments_eur: number;
  };
  last_event: string | null;
  /** Standing balance of the CURRENT billing period — deliberately not scoped
   *  to the selected window: a quota is a level, not a flow. */
  quota: {
    included_credits: number;
    used_credits_milli: number;
    remaining_credits_milli: number;
    /** Still available from purchases (not expired). */
    purchased_credits: number;
    /** Granted by those same purchases — granted − remaining = consumed.
     *  Purchases are drawn only after the plan quota is exhausted. */
    purchased_granted_credits: number;
    period_start: string | null;
  };
}

export interface CreditBreakdownRow {
  key: string;
  label: string;
  credits_milli: number;
  events: number;
  total_tokens: number;
  distinct_actors: number;
  /** null = no statement possible (rows without an actor), never 0. */
  avg_credits_milli_per_actor: number | null;
  /** What ONE execution typically costs. Median = the normal case; p90 = the
   *  upper end (turns above a token tier pull the mean, not the median). */
  avg_credits_milli_per_event: number | null;
  median_credits_milli_per_event: number | null;
  p90_credits_milli_per_event: number | null;
}

export interface CreditTimeseriesPoint {
  /** Local calendar day/week/month start (Europe/Berlin), no timezone. */
  bucket: string;
  series: string;
  /** Readable series name (for `activity`: the price list's display name). */
  label?: string;
  credits_milli: number;
  distinct_actors: number;
}

export type CreditGranularity = 'day' | 'week' | 'month';

export type CreditDimension =
  | 'bot'
  | 'course'
  | 'activity'
  | 'area'
  | 'client'
  | 'actor_kind'
  | 'model_setup'
  /** people of the organisation (portal users) — never learners by name */
  | 'user';

@Injectable({ providedIn: 'root' })
export class CreditAnalyticsService {
  private readonly base = `${environment.userBackend}/credit-analytics`;

  /** Last successful summary, so the page can show its period marker while a
   *  new request is in flight. Cleared on logout. */
  lastSummary: CreditSummary | null = null;

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService,
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  private toParams(filters: CreditFilters, extra: Record<string, any> = {}): HttpParams {
    let params = new HttpParams();
    const all: Record<string, any> = { ...filters, ...extra };
    Object.keys(all).forEach((key) => {
      const value = all[key];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  summary(filters: CreditFilters = {}): Observable<CreditSummary> {
    return this.http
      .get<{ data: CreditSummary }>(`${this.base}/summary`, { params: this.toParams(filters) })
      .pipe(map((r) => {
        this.lastSummary = r.data;
        return r.data;
      }));
  }

  breakdown(
    dimension: CreditDimension,
    filters: CreditFilters = {},
    limit = 50,
  ): Observable<CreditBreakdownRow[]> {
    return this.http
      .get<{ data: CreditBreakdownRow[] }>(`${this.base}/breakdown`, {
        params: this.toParams(filters, { dimension, limit }),
      })
      .pipe(map((r) => r.data));
  }

  timeseries(
    dimension: CreditDimension,
    filters: CreditFilters = {},
    granularity: CreditGranularity = 'day',
    topN = 8,
  ): Observable<CreditTimeseriesPoint[]> {
    return this.http
      .get<{ data: CreditTimeseriesPoint[] }>(`${this.base}/timeseries`, {
        params: this.toParams(filters, { dimension, granularity, top_n: topN }),
      })
      .pipe(map((r) => r.data));
  }

  private resetState(): void {
    this.lastSummary = null;
  }
}
